import { analytics as bloodhoundAnalytics } from "@sendgrid/bloodhound";
import { getAnalyticsId } from "./component";
import { init } from "./init";

export interface Identity {
  authToken?: string;
  traits?: object;
}

/**
 * Track Event is an object we pass to the tracking analytics to track an event.
 */
export interface TrackEvent {
  event: string;
  properties?: TrackEventProperties;
  callback?: () => void;
}

export interface TrackEventProperties {
  page?: string;
  location?: string;
  section?: string;
  subsection?: string;
  eventType?: string;
  elementType?: string;
  description?: string;
  [key: string]: string | number | boolean | undefined;
}

export interface ConfigureAnalyticsOptions {
  apiBaseUrl: string;
}

/**
 * The analytics component for tracking a user.
 */
export interface Analytics {
  init: () => void;
  identify: (identity: Identity) => Promise<void>;
  track: (data: TrackEvent) => void;
  configure: (options: ConfigureAnalyticsOptions) => void;
  getAnalyticsId: () => Promise<[string, string]>;
  getAnonymousId: () => string;
}

export function getAnonymousId() {
  // Segment generates an anonymousId for us when we call identify without an authToken.
  // We can retrieve it from the analytics object.
  return typeof window?.analytics?.user === "function"
    ? window.analytics?.user()?.anonymousId() || ""
    : "";
}

/**
 * This is the main analytics object that will be used to track events and identify users.
 */
export const analytics: Analytics = {
  init,
  identify: bloodhoundAnalytics.identify,
  track: bloodhoundAnalytics.track,
  configure: bloodhoundAnalytics.configure,
  getAnalyticsId,
  getAnonymousId,
};

/**
 * NoopAnalytics is a no-operation implementation of the Analytics interface.
 */
export const NoopAnalytics: Analytics = {
  init: (): void => {},
  identify: (identity: Identity): Promise<void> => {
    return Promise.resolve();
  },
  track: (data: TrackEvent): Promise<void> => {
    return Promise.resolve();
  },
  configure: (options: ConfigureAnalyticsOptions): void => {
    return;
  },
  getAnalyticsId,
  getAnonymousId,
};
